import axios from 'axios';
import config from '../../../config';
import { ApplicationDetailService } from '../types/outcomeTypes';

// Uses the hook returned by useAssistPointApiFetcher — pass fetchGET in directly
// so this service stays a plain function (no hook rules, easier to test).
type FetchGET = (request: string, practiceIdOverride?: number) => Promise<any>;

const SESSION_KEY = 'userProfileExternalObjectId';

// Module-level in-flight promise. If two callers invoke ensureUserProfileId
// concurrently (e.g. React StrictMode double-invoking effects), the second
// awaits the first rather than firing a second GetAssistPointUser request to
// the AP API. Sending two rapid-fire calls resets the AP API session mid-flight
// and causes the immediately following GetAuthorizedUserProfilePracticeOptions
// to return 204 (no content) even though the GUID header is correct.
let _inflightProfileId: Promise<void> | null = null;

/**
 * Ensures sessionStorage has a userProfileExternalObjectId before any practice-scoped
 * AP API call is made.  The AP API requires this header on every /practice/{id}/...
 * request; without it the server returns 403.
 *
 * GetAssistPointUser lives at the ROOT of the AP API (no /practice/ prefix) and can be
 * called with just the Auth0 bearer token — it returns the list of GUIDs the current
 * user is authorised for.  We store the first one so fetchGET spreads it automatically.
 *
 * No-ops if a value is already present (saves a round-trip on subsequent lookups).
 * Deduplicates concurrent callers via _inflightProfileId so only one network request
 * fires even when React StrictMode double-invokes the calling effect.
 */
export async function ensureUserProfileId(token: string): Promise<void> {
    // Deduplicate concurrent callers: wait for any in-flight request rather than
    // firing a second one. After it resolves the GUID will be in sessionStorage
    // so the check below will no-op.
    if (_inflightProfileId) {
        await _inflightProfileId;
        return;
    }

    if (sessionStorage.getItem(SESSION_KEY)) return;

    _inflightProfileId = (async () => {
        try {
            const response = await axios.get(
                `${config.apiUrl}/Authorization/GetAssistPointUser`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const raw = response.data?.authorizedExternalObjectIds;
            const ids: string[] = Array.isArray(raw)
                ? raw
                : Array.isArray(raw?.$values)
                    ? raw.$values
                    : [];

            if (ids.length > 0) {
                sessionStorage.setItem(SESSION_KEY, ids[0]);
            } else {
                console.warn('[ensureUserProfileId] GetAssistPointUser returned empty ids — GUID not stored');
            }
        } catch (err) {
            // Non-fatal — surfaces as 403 on the next call if it fails
            console.warn('ensureUserProfileId: could not bootstrap profile GUID', err);
        } finally {
            _inflightProfileId = null;
        }
    })();

    await _inflightProfileId;
}

export async function fetchApplicationServices(
    applicationId: number,
    fetchGET: FetchGET,
    practiceId?: number
): Promise<ApplicationDetailService[]> {
    return fetchGET(`Application/ApplicationServices/${applicationId}`, practiceId);
}

/**
 * Enriches services with existing enrollment data via two parallel waves:
 *   Wave 1: Reenrollment/GetEnrollmentDetailsFromOldEnrollment/{patientId}/{serviceId}
 *           → resolves enrollmentId, startDate, endDate, approvedAmount
 *   Wave 2: Application/Enrollment/{enrollmentId}
 *           → resolves memberId, bin, pcn, groupId
 * Services with no enrollment record are returned unchanged.
 */
export async function enrichServicesWithEnrollment(
    services: ApplicationDetailService[],
    fetchGET: FetchGET,
    practiceId?: number
): Promise<ApplicationDetailService[]> {
    // Wave 1 — get enrollmentId + dates/amounts per service
    const wave1 = await Promise.all(
        services.map(svc =>
            fetchGET(
                `Reenrollment/GetEnrollmentDetailsFromOldEnrollment/${svc.patientId}/${svc.applicationServiceId}`,
                practiceId
            ).catch(() => null)
        )
    );

    // Wave 2 — get RX fields for services that have an enrollmentId
    const wave2 = await Promise.all(
        wave1.map(reenroll =>
            reenroll?.enrollmentId
                ? fetchGET(`Application/Enrollment/${reenroll.enrollmentId}`, practiceId).catch(() => null)
                : Promise.resolve(null)
        )
    );

    return services.map((svc, i) => {
        const reenroll = wave1[i];
        const enrollment = wave2[i];
        if (!reenroll?.enrollmentId) return svc;
        return {
            ...svc,
            effectiveDateStart: enrollment?.startDate   ?? reenroll.startDate   ?? svc.effectiveDateStart,
            effectiveDateEnd:   enrollment?.endDate     ?? reenroll.endDate     ?? svc.effectiveDateEnd,
            awardAmount:        enrollment?.approvedAmount ?? reenroll.approvedAmount ?? svc.awardAmount,
            memberId:           enrollment?.memberId    ?? svc.memberId,
            rxBin:              enrollment?.bin         ?? svc.rxBin,
            rxPcn:              enrollment?.pcn         ?? svc.rxPcn,
            rxGroup:            enrollment?.groupId     ?? svc.rxGroup,
        };
    });
}
